import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect online/offline status with real internet connectivity checking
 * @returns {boolean} - Returns true if online, false if offline
 */
export const useOnlineStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const checkInProgressRef = useRef(false);
    const lastCheckTimeRef = useRef(0);

    // Function to check actual internet connectivity
    const checkOnlineStatus = async () => {
        // Prevent multiple simultaneous checks
        if (checkInProgressRef.current) {
            return;
        }

        checkInProgressRef.current = true;
        const now = Date.now();
        lastCheckTimeRef.current = now;

        // First, check navigator.onLine - if false, we're definitely offline
        if (!navigator.onLine) {
            console.log('?? navigator.onLine is false - OFFLINE');
            setIsOnline(false);
            checkInProgressRef.current = false;
            return false;
        }

        try {
            // Check actual internet connectivity by trying to reach an external reliable resource
            // We try multiple endpoints to ensure reliability
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.log('?? Request timeout - assuming OFFLINE');
                controller.abort();
            }, 2000);

            // Try to fetch a small external resource (Google's 1x1 pixel or DNS service)
            // This ensures we're checking ACTUAL internet, not just localhost
            const response = await fetch('https://dns.google/resolve?name=google.com&type=A', {
                method: 'HEAD',
                mode: 'no-cors', // Avoid CORS issues
                cache: 'no-cache',
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            
            // With no-cors mode, we can't read the response, but if fetch succeeds, we're online
            console.log('?? Internet connectivity check passed - ONLINE');
            setIsOnline(true);
            checkInProgressRef.current = false;
            return true;
        } catch (error) {
            // If fetch fails for any reason, we're offline
            console.log('?? Internet connectivity check failed:', error.name, '-', error.message);
            setIsOnline(false);
            checkInProgressRef.current = false;
            return false;
        }
    };

    useEffect(() => {
        const handleOnline = () => {
            console.log('?? Browser online event triggered');
            setIsOnline(true);
            checkOnlineStatus();
        };

        const handleOffline = () => {
            console.log('?? Browser offline event triggered');
            setIsOnline(false);
        };

        // Add event listeners for online/offline events
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // More aggressive periodic connectivity check - every 2 seconds
        const intervalId = setInterval(() => {
            checkOnlineStatus();
        }, 2000);

        // Initial check on mount
        checkOnlineStatus();

        // Cleanup event listeners and interval on unmount
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(intervalId);
        };
    }, []);

    return isOnline;
};
